import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { StyledPaper, StyledTable, StyledTableRow, StyledTableCell } from '../../../../uiComponents/MuiTable/StyledTable.js'
import MuiTableHeader from '../../../../uiComponents/MuiTable/MuiTableHeader.js';
import { stableSort, getComparator, handleRequestSort } from '../../../../uiComponents/MuiTable/MuiHelper';
import {
  TableBody,
  TableContainer,
  Button
} from '@mui/material';
import AnalyteFlagModal from '../AnalyteFlagModal/index.js';
import StatusIcon from '../../../../icons/StatusIcon.js';
// Important: row.code is being transformed to descAndShortName in getTableDataWithDescriptions

const PatientDetailsBiochemicalAnalyteTable = ({ data, selectedAnalyte, onSortChange }) => {
  if (!data || data.length === 0) return null;

  const rowRefs = useRef({});

  useEffect(() => {
    if (selectedAnalyte && rowRefs.current[selectedAnalyte]) {
      rowRefs.current[selectedAnalyte].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedAnalyte]);

  const [order, setOrder] = useState('asc'); //default sort direction
  const [orderBy, setOrderBy] = useState('code'); //default sort column for table
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRow, setModalRow] = useState(null);

  // Notify parent of sort state changes
  useEffect(() => {
    if (onSortChange) {
      onSortChange(order, orderBy);
    }
  }, [order, orderBy, onSortChange]);

  const handleOpenModal = (row) => {
    setModalRow(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalRow(null);
  };

  const headCells = [
    { label: 'Analyte Name', id: 'code', numeric: false, disablePadding: false },
    { label: 'Test Result', id: 'value', numeric: true, disablePadding: false, align: 'left' },     
    { label: 'Z-score', id: 'zScore', numeric: true, disablePadding: false, align: 'left' },
    { label: 'Instrument', id: 'instrument', numeric: false, disablePadding: false },
    { label: 'Reagent Kit', id: 'reagentKit', numeric: false, disablePadding: false },
    { label: 'Lot Number', id: 'lotNumber', numeric: false, disablePadding: false },
  ];

  return (
    <>
    <TableContainer component={StyledPaper} sx={{ maxHeight: 400, overflowY: 'auto' }}>
      <StyledTable 
        size='small'
        aria-label="test results table"
        key={JSON.stringify(data)} 
      >
        <MuiTableHeader 
          stickyHeader={true}
          headCells={headCells} 
          order={order}  
          orderBy={orderBy} 
          onRequestSort={(event, property) => handleRequestSort(event, property, order, orderBy, setOrder, setOrderBy)}
        />
        <TableBody>
          {stableSort(data, getComparator(order, orderBy))
            .map((row, index) => {
            return (
              <StyledTableRow
                key={index}
                ref={el => rowRefs.current[row.code] = el}
                selected={row.code === selectedAnalyte}
                sx={row.code === selectedAnalyte ? { backgroundColor: 'var(--accent-secondary-color) !important' } : {}}
              >
                <StyledTableCell style={{
                  width: '28%',
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word'
                }}>
                  {row.code}
                </StyledTableCell>
                <StyledTableCell>{row.value != null ? Number(row.value).toFixed(2) : ''}</StyledTableCell>
                <StyledTableCell>{row.zScore != null ? Number(row.zScore).toFixed(2) : ''}</StyledTableCell>
                <StyledTableCell style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '150px'
                }}>
                  {row.instrument || ''}
                </StyledTableCell>
                <StyledTableCell style={{
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '150px'
                }}>
                  {row.reagentKit || ''}
                </StyledTableCell>
                <StyledTableCell style={{
                  width: '10%',
                  wordBreak: 'break-all',
                  overflowWrap: 'break-word',
                  maxWidth: '120px'
                }}>
                  {row.lotNumber || ''}
                </StyledTableCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </StyledTable>
    </TableContainer>
    {modalOpen && (
      <AnalyteFlagModal
        open={modalOpen}
        onClose={handleCloseModal}
        row={modalRow}
      />
    )}
    </>
  );
};

PatientDetailsBiochemicalAnalyteTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      zScore: PropTypes.number.isRequired,
    })
  ).isRequired,
   selectedAnalyte: PropTypes.string,
   onSortChange: PropTypes.func,
};

export default PatientDetailsBiochemicalAnalyteTable;
